
import { useState } from "react";
import { 
  Check, 
  X, 
  Clock, 
  Copy, 
  ExternalLink,
  Archive,
  Send,
  Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/lib/toast";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ConfirmDeleteDialog } from "./components/ConfirmDeleteDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useApprovals, ApprovalPost } from "@/hooks/use-approvals";
import { CreateApprovalDialog } from "./components/CreateApprovalDialog";
import { ResubmitApprovalDialog } from "./components/ResubmitApprovalDialog";
import { Link } from "react-router-dom";

export default function Approvals() {
  const { 
    approvals, 
    isLoading, 
    updateApprovalStatus, 
    archiveApproval, 
    refreshApprovals
  } = useApprovals();
  const [searchTerm, setSearchTerm] = useState("");
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [approvalToArchive, setApprovalToArchive] = useState<string | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);
  const [approvalToResubmit, setApprovalToResubmit] = useState<ApprovalPost | null>(null);
  const isMobile = useIsMobile();
  
  const filterApprovals = (status: string) => {
    const filteredByStatus = status === "all" ? approvals : approvals.filter(item => item.status === status);
    
    if (!searchTerm) return filteredByStatus;
    
    return filteredByStatus.filter(item => 
      item.client_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };
  
  const pendingApprovals = filterApprovals("pending");
  const approvedItems = filterApprovals("approved");
  const rejectedItems = filterApprovals("rejected");
  
  const handleApprove = (id: string) => {
    updateApprovalStatus(id, "approved", "Aprovado sem alterações!");
  };
  
  const handleReject = (id: string, feedback: string) => {
    updateApprovalStatus(id, "rejected", feedback);
  };

  const openArchiveDialog = (id: string) => {
    setApprovalToArchive(id);
    setArchiveDialogOpen(true);
  };

  const handleArchive = async () => {
    if (approvalToArchive) {
      setIsArchiving(true);
      try {
        const success = await archiveApproval(approvalToArchive);
        if (success) {
          await refreshApprovals();
        }
      } catch (error) {
        console.error("Error in handleArchive:", error);
      } finally {
        setIsArchiving(false);
        setArchiveDialogOpen(false);
        setApprovalToArchive(null);
      }
    }
  };

  const openResubmitDialog = (approval: ApprovalPost) => {
    setApprovalToResubmit(approval);
    setResubmitDialogOpen(true);
  };

  const copyShareableLink = (approval: ApprovalPost) => {
    if (!approval.slug) {
      toast.error("Link não disponível para este post");
      return;
    }
    
    const link = `${window.location.origin}/approve/${approval.slug}`;
    
    navigator.clipboard.writeText(link)
      .then(() => toast.success("Link copiado para a área de transferência!"))
      .catch(() => toast.error("Erro ao copiar link"));
  };
  
  const openShareableLink = (approval: ApprovalPost) => {
    if (!approval.slug) {
      toast.error("Link não disponível para este post");
      return;
    }
    
    const link = `${window.location.origin}/approve/${approval.slug}`;
    window.open(link, "_blank");
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge variant="outline" className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800/50">
            <Clock className="h-3 w-3 mr-1" /> Pendente
          </Badge>
        );
      case "approved":
        return (
          <Badge variant="outline" className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800/50">
            <Check className="h-3 w-3 mr-1" /> Aprovado
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="outline" className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50">
            <X className="h-3 w-3 mr-1" /> Rejeitado
          </Badge>
        );
      default:
        return null;
    }
  };
  
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case "instagram":
        return "Instagram";
      case "facebook":
        return "Facebook";
      case "linkedin":
        return "LinkedIn";
      default:
        return "Outro";
    }
  };

  if (isLoading) {
    return (
      <div className="w-full py-4 sm:py-6 flex items-center justify-center">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="w-full py-4 sm:py-6">
      <ConfirmDeleteDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        title="Arquivar aprovação?"
        description="Tem certeza que deseja arquivar este post da lista de aprovações? O link de aprovação será desativado."
        onConfirm={handleArchive}
        loading={isArchiving}
        confirmText="Arquivar"
        cancelText="Cancelar"
      />
      
      <ResubmitApprovalDialog
        open={resubmitDialogOpen}
        onOpenChange={setResubmitDialogOpen}
        approval={approvalToResubmit}
        onResubmit={async (id: string, data: any) => true}
      />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Aprovações de Cliente</h1>
          <p className="text-muted-foreground">
            Gerencie o fluxo de aprovação de materiais pelos clientes
          </p>
        </div>
        
        <div className="flex gap-2 w-full sm:w-auto">
          <Link to="/arquivados" className="flex-1 sm:flex-none">
            <Button variant="outline" className="w-full sm:w-auto">
              <Archive className="mr-2 h-4 w-4" />
              Arquivados
            </Button>
          </Link>
          
          <CreateApprovalDialog
            onApprovalCreated={refreshApprovals}
            trigger={
              <Button className="flex-1 sm:flex-none">
                <Plus className="mr-2 h-4 w-4" />
                Nova Aprovação
              </Button>
            }
          />
        </div>
      </div>
      
      {/* Search */}
      <div className="mb-6">
        <Input
          placeholder="Buscar por cliente ou título..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>
      
      {/* Tabs */}
      <Tabs defaultValue="pending" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" className="text-xs sm:text-sm">
            Pendentes <Badge variant="secondary" className="ml-2">{pendingApprovals.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="approved" className="text-xs sm:text-sm">
            Aprovados <Badge variant="secondary" className="ml-2">{approvedItems.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="rejected" className="text-xs sm:text-sm">
            Rejeitados <Badge variant="secondary" className="ml-2">{rejectedItems.length}</Badge>
          </TabsTrigger>
        </TabsList>
        
        {/* Pending Tab */}
        <TabsContent value="pending">
          {pendingApprovals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {pendingApprovals.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base truncate">{item.title}</CardTitle>
                        <CardDescription className="truncate">Cliente: {item.client_name}</CardDescription>
                      </div>
                      <div className="flex gap-2 items-center ml-2">
                        {getStatusBadge(item.status)}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-amber-600 hover:text-amber-700 h-8 w-8"
                          onClick={() => openArchiveDialog(item.id)}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <div className="aspect-video overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800">
                      <img 
                        src={item.image_url || item.static_image || ''} 
                        alt={item.title} 
                        className="w-full h-full object-cover" 
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium">Plataforma:</span> {getPlatformIcon(item.platform)}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Data:</span> {format(new Date(item.created_at), "dd 'de' MMM", { locale: ptBR })}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <Button 
                        size="sm" 
                        className="bg-green-500 hover:bg-green-600 w-full"
                        onClick={() => handleApprove(item.id)}
                      >
                        <Check className="h-4 w-4 mr-2" /> Aprovar
                      </Button>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyShareableLink(item)}
                        >
                          <Copy className="h-4 w-4 mr-1" /> Copiar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openShareableLink(item)}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" /> Abrir
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border rounded-lg">
              <h3 className="text-xl font-medium mb-2">Nenhuma aprovação pendente</h3>
              <p className="text-muted-foreground">
                Todas as aprovações foram processadas ou não há itens pendentes no momento.
              </p>
            </div>
          )}
        </TabsContent>
        
        {/* Approved Tab */}
        <TabsContent value="approved">
          {approvedItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {approvedItems.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base truncate">{item.title}</CardTitle>
                        <CardDescription className="truncate">Cliente: {item.client_name}</CardDescription>
                      </div>
                      <div className="flex gap-2 items-center ml-2">
                        {getStatusBadge(item.status)}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-amber-600 hover:text-amber-700 h-8 w-8"
                          onClick={() => openArchiveDialog(item.id)}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <div className="aspect-video overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800">
                      <img 
                        src={item.image_url || item.static_image || ''} 
                        alt={item.title} 
                        className="w-full h-full object-cover" 
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium">Plataforma:</span> {getPlatformIcon(item.platform)}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Data:</span> {format(new Date(item.created_at), "dd 'de' MMM", { locale: ptBR })}
                      </div>
                    </div>
                    
                    {item.feedback && (
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md border border-green-100 dark:border-green-800/30">
                        <p className="text-sm font-medium text-green-800 dark:text-green-400">Feedback:</p>
                        <p className="text-sm text-green-700 dark:text-green-300">{item.feedback}</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyShareableLink(item)}
                      >
                        <Copy className="h-4 w-4 mr-1" /> Copiar
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openShareableLink(item)}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" /> Abrir
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border rounded-lg">
              <h3 className="text-xl font-medium mb-2">Nenhum item aprovado</h3>
              <p className="text-muted-foreground">
                Itens aprovados pelos clientes aparecerão aqui.
              </p>
            </div>
          )}
        </TabsContent>
        
        {/* Rejected Tab */}
        <TabsContent value="rejected">
          {rejectedItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {rejectedItems.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base truncate">{item.title}</CardTitle>
                        <CardDescription className="truncate">Cliente: {item.client_name}</CardDescription>
                      </div>
                      <div className="flex gap-2 items-center ml-2">
                        {getStatusBadge(item.status)}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-amber-600 hover:text-amber-700 h-8 w-8"
                          onClick={() => openArchiveDialog(item.id)}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <div className="aspect-video overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800">
                      <img 
                        src={item.image_url || item.static_image || ''} 
                        alt={item.title} 
                        className="w-full h-full object-cover" 
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium">Plataforma:</span> {getPlatformIcon(item.platform)}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Data:</span> {format(new Date(item.created_at), "dd 'de' MMM", { locale: ptBR })}
                      </div>
                    </div>
                    
                    {item.feedback && (
                      <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md border border-red-100 dark:border-red-800/30">
                        <p className="text-sm font-medium text-red-800 dark:text-red-400">Feedback:</p>
                        <p className="text-sm text-red-700 dark:text-red-300">{item.feedback}</p>
                      </div>
                    )}
                    
                    <div className="flex flex-col gap-2">
                      <Button 
                        size="sm" 
                        className="bg-primary hover:bg-primary/90 w-full"
                        onClick={() => openResubmitDialog(item)}
                      >
                        <Send className="h-4 w-4 mr-2" /> Enviar Novamente
                      </Button>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyShareableLink(item)}
                        >
                          <Copy className="h-4 w-4 mr-1" /> Copiar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openShareableLink(item)}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" /> Abrir
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border rounded-lg">
              <h3 className="text-xl font-medium mb-2">Nenhum item rejeitado</h3>
              <p className="text-muted-foreground">
                Itens rejeitados pelos clientes aparecerão aqui.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
